/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { StationDataObject, TripType } from '../types/FlightSearch.types';
import { flightKeys } from './flights';
import {
  CabinClassType,
  PassengerCount,
} from '../components/PassengerClass/types';
import { buildFlightSearchHistoryEntry } from '../util/FlightSearch.utils';

export const recentSearchesKey = flightKeys.recentSearches;

/**
 * Origin-level recent search
 * Dates are intentionally kept SEPARATE from destinations
 */
export type OriginWithDestinations = StationDataObject & {
  departDate?: Date | string;
  returnDate?: Date | string;
  passengers?: PassengerCount;
  destinations: StationDataObject[];
};

export interface CurrentMetaSearch {
  departDate?: Date | string;
  returnDate?: Date | string;
  passengers?: PassengerCount;
  cabinClass?: CabinClassType;
  tripType?: TripType;
}

/**
 * The unified store structure
 */
export interface RecentSearchesStore {
  recentSearches: OriginWithDestinations[];
  currentMetaSearch?: CurrentMetaSearch;
}

const defaultStore: RecentSearchesStore = {
  recentSearches: [],
  currentMetaSearch: undefined,
};

const MAX_RECENT_SEARCHES = 10;

/**
 * Strip unwanted / nested fields before persisting
 */
const sanitizeStationForPersist = (
  station: StationDataObject | null | undefined,
): StationDataObject | null => {
  if (!station) return null;

  return {
    iconName: station.iconName ?? '',
    CityCode: station.CityCode,
    CityName: station.CityName,
    CountryCode: station.CountryCode,
    CountryName: station.CountryName,
    StationCode: station.StationCode,
    StationName: station.StationName,
    Weightage: station.Weightage ?? 0,
    Lat: station.Lat ?? 0,
    Long: station.Long ?? 0,
    StationType: station.StationType,
    Dest: station.Dest ?? '',
    IsCity: !!station.IsCity,
    AirportName: station.AirportName ?? '',
    Stations: station.Stations ?? [],
    Provider: station.Provider,
    AAFlight: station.AAFlight,
  };
};

/**
 * Base station identity (used for destinations)
 */
const getStationKey = (s: StationDataObject) =>
  `${s.IsCity ? '1' : '0'}|${s.CityCode}|${s.StationCode}`;

/**
 * Origin identity MUST include dates
 */
const getOriginSearchKey = (s: StationDataObject) => getStationKey(s);

export interface CurrentSearchOverride {
  originObj?: StationDataObject;
  destinationObj?: StationDataObject;
  promoCode?: string;
}

const toDate = (value?: Date | string): Date | undefined => {
  if (!value) return undefined;
  return value instanceof Date ? value : new Date(value);
};

/**
 * Flattens the (origin -> multiple destinations) recent-searches store into
 * one search-history entry per origin/destination pair - the flat list
 * shape the super app's native bridge expects
 * (NativeModules.ReactToNative.sendSearchHistory), mirroring how the legacy
 * SearchForm.native.js's storeRecentSearchV2 sends the *whole* stored list
 * on every search, not just the one just submitted.
 *
 * Each origin's tripType is inferred from whether it has a returnDate,
 * since tripType isn't tracked per historical entry in this store (only
 * the most recent search's tripType is available, via currentMetaSearch).
 * The just-submitted search's promoCode is attached to its matching entry;
 * promoCode isn't otherwise persisted per historical entry either.
 */
export const flattenRecentSearchesForHistory = (
  store: RecentSearchesStore | undefined,
  currentSearch?: CurrentSearchOverride,
) => {
  if (!store?.recentSearches?.length) return [];

  return store.recentSearches.flatMap((origin) => {
    const departDate = toDate(origin.departDate);
    if (!departDate) return [];

    const returnDate = toDate(origin.returnDate);

    return origin.destinations.map((destination) =>
      buildFlightSearchHistoryEntry({
        originObj: origin,
        destinationObj: destination,
        departDate,
        returnDate,
        tripType: returnDate ? TripType.ROUND_TRIP : TripType.ONE_WAY,
        adultCount: origin.passengers?.adultCount ?? 0,
        childCount: origin.passengers?.childCount ?? 0,
        infantCount: origin.passengers?.infantCount ?? 0,
        promoCode:
          origin.StationCode === currentSearch?.originObj?.StationCode &&
          destination.StationCode === currentSearch?.destinationObj?.StationCode
            ? currentSearch?.promoCode
            : undefined,
      }),
    );
  });
};

/**
 * Returns the full store including historical searches and current meta
 */
export const useRecentSearches = () => {
  return useQuery<RecentSearchesStore>({
    queryFn: () => ({ recentSearches: [] }),
    queryKey: recentSearchesKey,
    staleTime: Infinity,
    gcTime: Infinity,
    enabled: false,
  });
};

/**
 * Add recent search (origin + dates + destination)
 */
export const useAddRecentSearchMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      origin?: StationDataObject;
      destination?: StationDataObject;
      departDate?: Date | string;
      returnDate?: Date | string;
      passengers?: PassengerCount;
      cabinClass?: CabinClassType;
      tripType?: TripType;
    }) => payload,

    onMutate: async (payload) => {
      const {
        origin,
        destination,
        departDate,
        returnDate,
        passengers,
        cabinClass,
        tripType,
      } = payload;
      const persistedReturnDate =
        tripType === TripType.ROUND_TRIP ? returnDate : undefined;

      await queryClient.cancelQueries({ queryKey: recentSearchesKey });
      const previous =
        queryClient.getQueryData<RecentSearchesStore>(recentSearchesKey) ??
        defaultStore;

      // 1. Always update Meta, even if stations are missing
      const newMeta: CurrentMetaSearch = {
        departDate,
        returnDate: persistedReturnDate,
        passengers,
        cabinClass,
        tripType,
      };

      const nextList = [...previous.recentSearches];

      // 2. Only update the list if both stations exist
      const o = sanitizeStationForPersist(origin);
      const d = sanitizeStationForPersist(destination);

      if (o && d) {
        const originKey = getOriginSearchKey(o);
        const destKey = getStationKey(d);

        const idx = nextList.findIndex(
          (item) => getOriginSearchKey(item) === originKey,
        );

        if (idx === -1) {
          nextList.unshift({
            ...o,
            departDate,
            returnDate: persistedReturnDate,
            passengers,
            destinations: [d],
          });
        } else {
          const existing = nextList[idx];
          const hasDest = existing.destinations.some(
            (x) => getStationKey(x) === destKey,
          );
          nextList.splice(idx, 1);
          nextList.unshift({
            ...existing,
            departDate,
            returnDate: persistedReturnDate,
            passengers,
            destinations: hasDest
              ? existing.destinations
              : [d, ...existing.destinations],
          });
        }
      }

      const nextStore: RecentSearchesStore = {
        recentSearches: nextList.slice(0, MAX_RECENT_SEARCHES),
        currentMetaSearch: newMeta,
      };

      // 3. Update the cache
      queryClient.setQueryData(recentSearchesKey, nextStore);

      return { previous };
    },

    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(recentSearchesKey, ctx.previous);
      }
    },
  });
};

/**
 * Clear recent searches (persisted cache entry)
 *
 * This will:
 * - Immediately update the UI (setQueryData to [])
 * - Remove the query entirely from React Query cache (removeQueries)
 *
 * Because this query key contains QUERY_PERSIST_CACHE, it will also be cleared
 * from the persisted store on next persist cycle.
 */
export const useClearRecentSearchesMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => true,

    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: recentSearchesKey });

      const previous =
        queryClient.getQueryData<RecentSearchesStore>(recentSearchesKey);

      // Use setQueryData to reset to defaultStore instead of removing the query entirely
      queryClient.setQueryData(recentSearchesKey, defaultStore);

      return { previous };
    },
    // Only remove the query on success if you want to wipe the persisted storage
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: recentSearchesKey, exact: true });
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(recentSearchesKey, ctx.previous);
      }
    },
  });
};
